<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { NuxtError } from '#app'

const props = defineProps<{
  error: NuxtError
}>()

const { t } = useI18n()
const route = useRoute()

const statusCode = computed(() => {
  if (typeof props.error?.statusCode === 'number') return props.error.statusCode
  if (props.error?.statusCode) return parseInt(props.error.statusCode as unknown as string, 10)
  return 500
})

const errorData = computed(() => {
  const code = statusCode.value
  
  if (code === 429) {
    return {
      title: t('errorPage.codes.rateLimited.title'),
      description: t('errorPage.codes.rateLimited.description'),
      icon: 'i-lucide-clock'
    }
  }
  
  if (code === 401) {
    return {
      title: t('errorPage.codes.unauthorized.title'),
      description: t('errorPage.codes.unauthorized.description'),
      icon: 'i-lucide-lock'
    }
  }
  
  if (code === 403) {
    return {
      title: t('errorPage.codes.forbidden.title'),
      description: t('errorPage.codes.forbidden.description'),
      icon: 'i-lucide-shield-alert'
    }
  }
  
  if (code === 404) {
    return {
      title: t('errorPage.codes.notFound.title'),
      description: t('errorPage.codes.notFound.description'),
      icon: 'i-lucide-search'
    }
  }
  
  if (code >= 500 && code < 502) {
    return {
      title: t('errorPage.codes.server.title'),
      description: t('errorPage.codes.server.description'),
      icon: 'i-lucide-server'
    }
  }
  
  if (code >= 502 && code <= 504) {
    return {
      title: t('errorPage.codes.unavailable.title'),
      description: t('errorPage.codes.unavailable.description'),
      icon: 'i-lucide-cloud-off'
    }
  }
  
  return {
    title: t('errorPage.codes.generic.title'),
    description: t('errorPage.codes.generic.description'),
    icon: 'i-lucide-triangle-alert'
  }
})

const handleRetry = () => {
  clearError({ redirect: route?.fullPath || '/' })
}

const handleHome = () => {
  clearError({ redirect: '/' })
}
</script>

<template>
  <div class="tq-error-layout">
    <div class="tq-error-card">
      <div class="tq-error-header">
        <div class="tq-logo">
          <span class="tq-mark">360</span>
        </div>
        <div class="tq-status-badge">
          {{ t('errorPage.statusLabel', { statusCode }) }}
        </div>
      </div>

      <div class="tq-error-content">
        <UIcon :name="errorData.icon" class="tq-error-icon" />
        <h1 class="tq-error-title">{{ errorData.title }}</h1>
        <p class="tq-error-description">{{ errorData.description }}</p>
      </div>

      <div class="tq-error-actions">
        <UButton
          v-if="statusCode !== 404 && statusCode !== 401 && statusCode !== 403"
          color="neutral"
          variant="ghost"
          icon="i-lucide-rotate-cw"
          @click="handleRetry"
        >
          {{ t('errorPage.retry') }}
        </UButton>
        <UButton
          color="primary"
          size="lg"
          icon="i-lucide-home"
          @click="handleHome"
        >
          {{ t('errorPage.goHome') }}
        </UButton>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tq-error-layout {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background-color: var(--tq-bg, #f9fafb);
  font-family: var(--tq-font-sans, ui-sans-serif, system-ui, sans-serif);
}

.tq-error-card {
  width: 100%;
  max-width: 28rem;
  background-color: var(--tq-bg-elevated, #ffffff);
  border: 1px solid var(--tq-border, #e5e7eb);
  border-radius: var(--tq-radius-xl, 1rem);
  padding: 2.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.tq-error-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.tq-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.5rem;
  height: 3.5rem;
  background: var(--tq-accent-chip-bg, #f3f4f6);
  border-radius: var(--tq-radius-lg, 0.75rem);
}

.tq-mark {
  font-weight: 700;
  font-size: 1.25rem;
  color: var(--tq-accent-readable, #111827);
  letter-spacing: -0.05em;
}

.tq-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  background-color: var(--tq-accent-chip-bg, #f3f4f6);
  color: var(--tq-accent-readable, #374151);
  font-size: 0.875rem;
  font-weight: 400;
  border-radius: 9999px;
  font-variant-numeric: tabular-nums;
}

.tq-error-content {
  margin-bottom: 2.5rem;
}

.tq-error-icon {
  width: 3rem;
  height: 3rem;
  color: var(--tq-text-muted, #9ca3af);
  margin-bottom: 1.5rem;
}

.tq-error-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--tq-text, #111827);
  margin: 0 0 0.75rem 0;
  letter-spacing: -0.025em;
}

.tq-error-description {
  font-size: 1rem;
  color: var(--tq-text-muted, #6b7280);
  margin: 0;
  line-height: 1.5;
  max-width: 20rem;
}

.tq-error-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
}

@media (min-width: 640px) {
  .tq-error-actions {
    flex-direction: row;
    justify-content: center;
  }
}
</style>
